import { useEffect } from "react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionLabel, StickyNote } from "@/components/Notebook";
import { Tick } from "@/components/Annotation";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";

/**
 * ScoreApp inline embed for the Presentation Readiness Diagnostic.
 * The script mounts anything that matches div[data-sa-url]; we load it once.
 */
const SCOREAPP_URL = "https://cf1b5be9-4ee4-458c-8a32-81bd5989bde2.scoreapp.com";

const ScoreAppEmbed = () => {
  useEffect(() => {
    const src = "https://static.scoreapp.com/js/integration/v1/embedding.js?v=pBiXnE";
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      // Re-run initialisation on re-navigation
      if (window.saInit) window.saInit();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-3 md:p-5" data-testid="scoreapp-embed-wrapper">
      <div
        data-sa-url={SCOREAPP_URL}
        data-sa-view="inline"
        data-sa-auto-height="1"
        style={{ maxWidth: "100%", width: "100%" }}
        data-testid="scoreapp-embed"
      />
    </div>
  );
};

const WHY = [
  "A short, honest self-assessment — 3 minutes.",
  "Immediate results: your presentation readiness score across the layers that matter.",
  "Personalised recommendations for what to work on before your event.",
];

export default function PresentationReadinessDiagnostic() {
  return (
    <>
      <PageMeta
        titleKey="meta.presentation_readiness_title"
        descriptionKey="meta.presentation_readiness_description"
        path={ROUTES.presentationReadinessDiagnostic}
        noindex
      />
      <PageHero
        testId="diagnostic-hero"
        eyebrow="Free diagnostic"
        headline={["How ready are you", "for your next presentation?"]}
        emphasis="A 3-minute check-in."
        emphasisAccent="underline"
        intro="Answer a handful of honest questions about your upcoming presentation. You'll get an instant readiness score and personalised recommendations you can apply immediately."
      />

      <section className="relative z-10 pb-24 md:pb-32" data-testid="diagnostic-body">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4 order-2 lg:order-1">
            <SectionLabel className="mb-5 block">what you get</SectionLabel>
            <ul className="space-y-4 mb-8">
              {WHY.map((e) => (
                <li key={e} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{e}</span>
                </li>
              ))}
            </ul>
            <StickyNote color="#E7DDC8" rotate={-3} className="inline-block">
              your body already knows what needs rehearsal — this makes it visible
            </StickyNote>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8 order-1 lg:order-2">
            <ScoreAppEmbed />
          </Reveal>
        </div>
      </section>
    </>
  );
}
